/**
 * KYRO / Kaching Bundles: show the selected variant image in the native
 * Shopify product gallery without mutating the selected bundle line items.
 *
 * Kaching's official variant-selected event supplies event.detail.variantId.
 * In Buy-3 offers, only the preview follows the last selected variant; the
 * app remains fully responsible for its independent variant selections.
 */
(function () {
  'use strict';

  if (window.__kyroKachingMediaIntegrationInstalled) return;
  window.__kyroKachingMediaIntegrationInstalled = true;

  const boundWidgets = new WeakSet();
  const variantCache = new WeakMap();
  const productSectionSelector = '[id^="MainProduct-"]';

  function findProductSection(widget) {
    return widget.closest(productSectionSelector) || document.querySelector(productSectionSelector);
  }

  function getVariants(section) {
    const picker = section.querySelector('variant-radios, variant-selects');
    if (!picker) return [];
    const script = picker.querySelector('script[type="application/json"]');
    if (!script) return [];
    const existing = variantCache.get(script);
    if (existing) return existing;
    try {
      const variants = JSON.parse(script.textContent);
      if (Array.isArray(variants)) {
        variantCache.set(script, variants);
        return variants;
      }
    } catch (_) {
      // A malformed or missing variant map must not interrupt the purchase flow.
    }
    return [];
  }

  function showVariantImage(widget, variantId) {
    if (!variantId) return;
    const section = findProductSection(widget);
    if (!section) return;
    const variant = getVariants(section).find(v => String(v.id) === String(variantId));
    const mediaId = variant && variant.featured_media && variant.featured_media.id;
    if (!mediaId) return;
    const sectionId = section.dataset.section || section.id.replace(/^MainProduct-/, '');
    if (!sectionId) return;
    const target = `${sectionId}-${mediaId}`;
    section.querySelectorAll('media-gallery').forEach((gallery) => {
      // Avoid an exception if Shopify has changed the media set or the product
      // does not actually include this variant's linked image.
      if (typeof gallery.setActiveMedia !== 'function') return;
      const activeElement = Array.from(gallery.querySelectorAll('[data-media-id]'))
        .find(el => el.dataset.mediaId === target);
      if (!activeElement) return;
      // The theme handles thumbnails, hidden variant images and slider state.
      try { gallery.setActiveMedia(target, true); } catch (_) { /* No cart impact. */ }
    });
  }

  function bindWidget(widget) {
    if (!widget || boundWidgets.has(widget)) return;
    boundWidgets.add(widget);
    widget.addEventListener('variant-selected', (event) => {
      const variantId = event && event.detail && event.detail.variantId;
      showVariantImage(widget, variantId);
    });
  }

  function bindExistingWidgets(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    if (root.matches && root.matches('kaching-bundles-block')) bindWidget(root);
    root.querySelectorAll('kaching-bundles-block').forEach(bindWidget);
  }

  // The app may initialize after the theme, and the Shopify theme editor can
  // re-render a product section without a full page reload.
  document.addEventListener('kaching-bundles-block-loaded', (event) => {
    if (event.detail && event.detail.component) bindWidget(event.detail.component);
  });
  document.addEventListener('shopify:section:load', (event) => {
    bindExistingWidgets(event.target);
  });

  function init() {
    bindExistingWidgets(document);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1) bindExistingWidgets(node);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
